/* Altissimo — collection & search filtering without page reloads. */
(() => {
  'use strict';

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.debouncedPrice = debounce((form) => this.render(this.urlFromForm(form)), 700);

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-sort-toolbar]')) {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', e.target.value);
          this.render(url.toString());
          return;
        }
        const form = e.target.closest('[data-facets-form]');
        if (!form || e.target.matches('[data-price-input]')) return;
        this.render(this.urlFromForm(form));
      });

      this.addEventListener('input', (e) => {
        if (!e.target.matches('[data-price-input]')) return;
        this.debouncedPrice(e.target.closest('[data-facets-form]'));
      });

      this.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-facets-form]');
        if (!form) return;
        e.preventDefault();
        this.render(this.urlFromForm(form));
      });

      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (!link) return;
        e.preventDefault();
        this.render(link.href);
      });

      this.onPop = () => this.render(window.location.href, false);
      window.addEventListener('popstate', this.onPop);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }

    urlFromForm(form) {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        if (value !== '') params.append(key, value);
      }
      if (!params.has('sort_by')) {
        const current = new URLSearchParams(window.location.search).get('sort_by');
        if (current) params.set('sort_by', current);
      }
      const query = params.toString();
      return `${window.location.pathname}${query ? `?${query}` : ''}`;
    }

    async render(href, push = true) {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      const fetchUrl = new URL(url.toString());
      fetchUrl.searchParams.set('section_id', this.sectionId);

      const results = this.querySelector('[data-facets-results]');
      if (results) results.classList.add('is-loading');
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      try {
        const res = await fetch(fetchUrl.toString(), { signal: this.controller.signal });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

        this.querySelectorAll('[data-facets-update]').forEach((el) => {
          const fresh = doc.getElementById(el.id);
          if (!fresh) return;
          const preserve = el.hasAttribute('data-preserve-open');
          const openIds = preserve ? [...el.querySelectorAll('details')].filter((d) => d.open).map((d) => d.id) : null;
          const scroll = el.scrollTop;
          el.innerHTML = fresh.innerHTML;
          if (preserve) {
            el.querySelectorAll('details').forEach((d) => { d.open = openIds.includes(d.id); });
            el.scrollTop = scroll;
          }
        });

        const toolbarSort = this.querySelector('[data-sort-toolbar]');
        if (toolbarSort && url.searchParams.get('sort_by')) toolbarSort.value = url.searchParams.get('sort_by');

        if (push) window.history.pushState({ facets: true }, '', `${url.pathname}${url.search}`);

        // On desktop, bring the top of the results into view if the visitor scrolled far down.
        const top = this.getBoundingClientRect().top;
        if (top < 0 && window.matchMedia('(min-width: 990px)').matches) {
          window.scrollTo({ top: window.scrollY + top - 80, behavior: 'smooth' });
        }
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = url.toString();
      } finally {
        const fresh = this.querySelector('[data-facets-results]');
        if (fresh) fresh.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);
})();
